/**
 * Who the clients and benefactors of a Case or Lead are (OWA-1341).
 *
 * Every screen that needs a case's payers, benefactor emails or payment eligibility asks this
 * module, so the rules live in one place:
 *
 * - A participant is a benefactor of the Case or Lead it belongs to when its relationships
 *   include the Benefactor lookup (matched by id, never by the lookup's editable key) and it is
 *   not also adverse (OWA-886).
 * - A Lead's payers are benefactors too: converting the Lead makes each one a Benefactor
 *   participant of the new Case (LeadCard.participantHelpers). A payer the firm also marked as
 *   adverse on that Lead is dropped, as the conversion drops it.
 * - Lead clients are clients, not benefactors.
 *
 * Inline joins over the participants slice drifted from these rules screen by screen (matter ids
 * looked up as leads, lead clients counted as benefactors, adverse benefactors kept), so new code
 * should use these helpers instead of filtering participants itself.
 */
import store from '@/store/store';
import LookupRegistry from '@/ui/LookupSelect/LookupRegistry';
import type {uuid} from '@/@types/entity';
import type Lead from '@/entities/lead';
import type Matter from '@/entities/matter';
import type Participant from '@/entities/participant';
import type ArrayMap from '@/lib/ArrayMap';

export type ParticipantSource = ArrayMap<Participant> | Participant[];
export type CaseOrLead = Matter | Lead;
type CaseOrLeadList = ArrayMap<Matter> | ArrayMap<Lead> | ArrayMap<CaseOrLead> | (CaseOrLead | null | undefined)[];

const distinct = (ids: (uuid | null | undefined)[]): uuid[] => [...new Set(ids.filter((id): id is uuid => !!id))];

const toList = (entities: CaseOrLeadList | null | undefined): CaseOrLead[] => {
    if (!entities) return [];
    return (entities as (CaseOrLead | null | undefined)[]).filter((e): e is CaseOrLead => e != null);
};

/** A Lead carries payers; a Case (Matter) carries matterId. */
export function isLeadEntity(entity: CaseOrLead): entity is Lead {
    return !('matterId' in entity) && Array.isArray((entity as Lead).payers);
}

/** The participants of one Case, Lead or Contact, excluding deleted rows. */
export function participantsOf(entityId: uuid, participants?: ParticipantSource): Participant[] {
    const source = participants ?? store.getState().participants;
    const rows = 'getMappingFor' in source && typeof source.getMappingFor === 'function'
                 ? source.getMappingFor('entityId', entityId)
                 : (source as Participant[]).filter(p => p.entityId === entityId);
    return rows.filter(p => p.status !== 'D');
}

/** Is this participant a benefactor (Benefactor relationship, and not also adverse)? */
export function isBenefactorParticipant(participant: Pick<Participant, 'relationships'>): boolean {
    return !!participant.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR)
        && !LookupRegistry.hasBenefactorConflict(participant.relationships);
}

const isAdverseParticipant = (participant: Pick<Participant, 'relationships'>): boolean =>
    !!participant.relationships?.some(id => LookupRegistry.isAdverseRelationship(id));

/**
 * Benefactor contact ids of one Case, Lead or Contact record: Benefactor participants, plus the
 * given payers, minus anyone the firm marked adverse on that record. A contact can hold
 * Benefactor and an adverse role on separate participant rows (a contact merge keeps both rows),
 * so the adverse check spans every row of the record, not just the Benefactor row.
 */
function benefactorsOf(entityId: uuid, payers: uuid[], participants?: ParticipantSource): uuid[] {
    const rows = participantsOf(entityId, participants);
    const adverse = new Set(rows.filter(isAdverseParticipant).map(p => p.contactId));
    return rows.filter(isBenefactorParticipant).map(p => p.contactId)
        .concat(payers)
        .filter(cid => !adverse.has(cid));
}

/**
 * Benefactor contact ids from the participants of the given entity ids only. Use this when only
 * ids are known (an invoice's lead id, say); prefer getBenefactorIds when the entities are.
 */
export function getParticipantBenefactorIds(entityIds: (uuid | null | undefined)[], participants?: ParticipantSource): uuid[] {
    return distinct(distinct(entityIds).flatMap(id => benefactorsOf(id, [], participants)));
}

/**
 * Benefactor contact ids for the given Cases and Leads: their Benefactor participants plus each
 * Lead's payers, never anyone adverse. Missing entries are ignored.
 */
export function getBenefactorIds(entities: CaseOrLeadList | null | undefined, participants?: ParticipantSource): uuid[] {
    return distinct(toList(entities).flatMap(entity =>
        benefactorsOf(entity.id, isLeadEntity(entity) ? entity.payers ?? [] : [], participants)));
}

/**
 * Every Benefactor participant of a Case, adverse or not, in store order. Only a finalized
 * invoice's payment link uses this: a finalized invoice never changes, so its link keeps the
 * benefactors it listed before OWA-886 filtered adverse ones (invoicePdf.buildQrCodeData).
 */
export function getFrozenLinkBenefactorIds(matter: Matter | null | undefined, participants?: ParticipantSource): uuid[] {
    if (!matter) return [];
    const source = participants ?? store.getState().participants;
    return (source as Participant[])
        .filter(p => p.entityId === matter.id && p.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR))
        .map(p => p.contactId);
}

/** Client contact ids for the given Cases and Leads. */
export function getClientIds(entities: CaseOrLeadList | null | undefined): uuid[] {
    return distinct(toList(entities).flatMap(entity => entity.clients ?? []));
}

/** Everyone who may pay for the given Cases and Leads: clients and benefactors. */
export function getPayerIds(entities: CaseOrLeadList | null | undefined, participants?: ParticipantSource): uuid[] {
    return distinct([...getClientIds(entities), ...getBenefactorIds(entities, participants)]);
}

/**
 * Store-wide payer and benefactor sets, rebuilt only when the leads, matters or participants
 * slice changes. Reducers replace a slice on every change, so identity is enough; the Contacts
 * list asks once per row and would otherwise rescan every Case for each contact.
 */
let storeWide: {
    leads: unknown, matters: unknown, participants: unknown,
    payers: Set<uuid>, benefactors: Set<uuid>,
} | null = null;

function storeWideSets() {
    const {leads, matters, participants} = store.getState();
    if (!storeWide || storeWide.leads !== leads || storeWide.matters !== matters || storeWide.participants !== participants) {
        const entities: CaseOrLead[] = [...leads.filter(() => true), ...matters.filter(() => true)];
        const benefactors = getBenefactorIds(entities, participants);
        storeWide = {
            leads, matters, participants,
            benefactors: new Set(benefactors),
            payers: new Set([...getClientIds(entities), ...benefactors]),
        };
    }
    return storeWide;
}

/**
 * Is the contact a client or benefactor of any Case or Lead in the store (or of the unsaved
 * entity being edited)? Payment processors only take payments from these people.
 */
export function isCasePayer(contactId: uuid, unsavedEntity?: CaseOrLead): boolean {
    if (!contactId) return false;
    if (storeWideSets().payers.has(contactId)) return true;
    return !!unsavedEntity && getPayerIds([unsavedEntity]).includes(contactId);
}

/** Is the contact a benefactor of any Case or Lead in the store? Lead clients are not. */
export function isBenefactorOfAny(contactId: uuid): boolean {
    if (!contactId) return false;
    return storeWideSets().benefactors.has(contactId);
}

/**
 * The Cases and Leads a contact belongs to, as a client or as a participant, each listed once.
 */
export function getCasesAndLeadsForContact<M extends Matter, L extends Lead>(
    contactId: uuid,
    matters: ArrayMap<M>,
    leads: ArrayMap<L>,
    participants: ParticipantSource,
): {cases: M[], leads: L[]} {
    const rows = 'getMappingFor' in participants && typeof participants.getMappingFor === 'function'
                 ? participants.getMappingFor('contactId', contactId)
                 : (participants as Participant[]).filter(p => p.contactId === contactId);
    const active = rows.filter(p => p.status !== 'D');

    const cases = new Map<uuid, M>();
    matters.filter(m => !!m.clients?.includes(contactId)).forEach(m => cases.set(m.id, m));
    active.filter(p => p.entityType === 'matter')
        .map(p => matters.get(p.entityId))
        .forEach(m => { if (m) cases.set(m.id, m); });

    const _leads = new Map<uuid, L>();
    leads.filter(l => !!l.clients?.includes(contactId)).forEach(l => _leads.set(l.id, l));
    active.filter(p => p.entityType === 'lead')
        .map(p => leads.get(p.entityId))
        .forEach(l => { if (l) _leads.set(l.id, l); });

    return {cases: [...cases.values()], leads: [..._leads.values()]};
}
